/**
 * Web Search Service
 *
 * Handles communication with the web search API endpoints for
 * DOE 810 compliant web search approval flow.
 */

import axios, { AxiosError } from "axios";
import { nuclearn_api_url } from "src/App";
import { RecencyFilter } from "src/views/assistant/types";

interface AxiosErrorData {
  detail?: string;
  message?: string;
}

/**
 * Extract a user-friendly error message from an Axios error response.
 */
function extractErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<AxiosErrorData>;
    const detail = axiosError.response?.data?.detail;
    const message = axiosError.response?.data?.message;
    if (detail) return detail;
    if (message) return message;
    if (axiosError.response?.statusText) {
      return `${axiosError.response.status}: ${axiosError.response.statusText}`;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
}

export interface WebSearchResult {
  title: string;
  url: string;
  snippet: string;
  published_date?: string;
}

export interface WebSearchResponse {
  type: "web_search_results";
  action_id: number;
  query: string;
  results: WebSearchResult[];
  total_results?: number;
  provider: string;
  markdown: string;
  final_recency?: RecencyFilter | null;
  final_max_results?: number;
}

export interface WebSearchErrorResponse {
  type: "web_search_error";
  action_id: number;
  query: string;
  error: string;
  error_type?: string;
  is_retryable: boolean;
  final_recency?: RecencyFilter | null;
  final_max_results?: number;
}

export interface WebSearchRejectedResponse {
  type: "web_search_rejected";
  action_id: number;
  message: string;
  reason?: string;
}

export interface WebSearchStatusResponse {
  available: boolean;
  provider?: string;
}

/**
 * Approve and execute a web search.
 *
 * @param actionId - The ExternalAction ID from the approval prompt
 * @param query - The (potentially edited) query to execute
 * @returns Search results for conversation continuation
 * @throws Error with user-friendly message on failure
 */
export async function approveWebSearch(
  actionId: number,
  query: string
): Promise<WebSearchResponse> {
  if (!actionId || actionId <= 0) {
    throw new Error("Invalid action ID");
  }
  if (!query?.trim()) {
    throw new Error("Search query cannot be empty");
  }

  try {
    const response = await axios.post<WebSearchResponse>(
      `${nuclearn_api_url}/web-search/approve/${actionId}`,
      { query }
    );
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Failed to execute web search"));
  }
}

/**
 * Reject a web search request.
 *
 * @param actionId - The ExternalAction ID to reject
 * @param reason - Optional rejection reason
 * @returns Rejection confirmation
 * @throws Error with user-friendly message on failure
 */
export async function rejectWebSearch(
  actionId: number,
  reason?: string
): Promise<WebSearchRejectedResponse> {
  if (!actionId || actionId <= 0) {
    throw new Error("Invalid action ID");
  }

  try {
    const response = await axios.post<WebSearchRejectedResponse>(
      `${nuclearn_api_url}/web-search/reject/${actionId}`,
      { reason }
    );
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Failed to cancel web search"));
  }
}

/**
 * Check if web search is available and configured.
 *
 * @returns Availability status and provider info
 */
export async function getWebSearchStatus(): Promise<WebSearchStatusResponse> {
  const response = await axios.get<WebSearchStatusResponse>(
    `${nuclearn_api_url}/web-search/status`
  );
  return response.data;
}
